import React, { useEffect, useRef } from 'react';
import './ProjectWizardPage.scss';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import usePermission from '../../../shared/hooks/global/UsePermission';
import DomainStep from './components/domain-step/DomainStep';
import SecondStep from './components/second-step/SecondStep';
import { useProjectWizard } from './contexts/ProjectWizardContext';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AGENCY_HUB,
  PROJECTS,
  WIZARD_RETURN_AGENCY_HUB,
  WIZARD_RETURN_PARAM,
} from '../../../shared/routes/path';
import Loader from '../../../shared/components/common/loader/Loader';
import TestimonialSlider from '../../../shared/components/testimonial-slider/TestimonialSlider';
import FirmNameStep from './components/firm-name-step/FirmNameStep';
import useRedirectWhenInvalidStatus from '../../../shared/hooks/account/UseRedirectWhenInvalidStatus';
import ThirdStep from './components/third-step/ThirdStep';
import useCanAddProject from '../../../shared/hooks/account/UseAddProject';

const ProjectWizardPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Opened from the Agency Hub: "back" leads there, not to the projects list.
  const returnsToAgencyHub = searchParams.get(WIZARD_RETURN_PARAM) === WIZARD_RETURN_AGENCY_HUB;
  const { accountResponse } = useAccount();
  const { canAddProject, loading } = useCanAddProject();
  const projectsAccess = usePermission('PROJECTS');

  const navigation = useNavigate();
  const { activeStep } = useProjectWizard();

  useRedirectWhenInvalidStatus();

  useEffect(() => {
    if (!loading && canAddProject === false) {
      navigation(PROJECTS);
    }
  }, [loading, canAddProject, navigation]);

  // Konto wczytane choć raz. Kontekst zeruje `accountResponse` przy każdym odświeżeniu (websocket,
  // zmiana planu), a podmiana kreatora na loader w TAMTYM momencie odmontowałaby kroki razem z tym,
  // co użytkownik zdążył wpisać — czekamy więc tylko na pierwsze wczytanie.
  const hadAccount = useRef(false);
  useEffect(() => {
    if (accountResponse) {
      hadAccount.current = true;
    }
  }, [accountResponse]);

  // Po rejestracji z zaproszenia logowanie przerzuca „nowe konto" prosto na kreator, a gość nie ma
  // prawa tworzyć projektów. Odesłanie musi zapaść W RENDERZE: z `useEffect` kreator zdążył się
  // pokazać na jedną klatkę, czyli dokładnie to mignięcie modala, które widać było po wejściu
  // z adresu. Dopóki konto się wczytuje, nie ma z czego rozstrzygnąć uprawnienia — `usePermission`
  // przy braku danych odmawia, więc zamiast odsyłać każdego nowego użytkownika czekamy na dane.
  if (!accountResponse && !hadAccount.current) {
    return (
      <div className="project-wizard new-wizard">
        <div className="project-wizard__content">
          <div className="page-content">
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  if (accountResponse && !projectsAccess.write) {
    return <Navigate to={PROJECTS} replace />;
  }

  return (
    <div className="project-wizard new-wizard">
      <div
        className={`${
          activeStep === 'QUESTIONS' ? 'project-wizard__page--second' : ''
        } project-wizard__page`}
      >
        <main>
          <>
            <div className="project-wizard__belt">
              <div className="logo">
                <div className="logo-box" />
              </div>
              {accountResponse && (
                <NavLink to={returnsToAgencyHub ? AGENCY_HUB : PROJECTS} className="btn-link">
                  <span className="material-symbols-outlined">arrow_back_ios</span>
                  {t(returnsToAgencyHub ? 'backToAgencyHub' : 'backToProjects')}
                </NavLink>
              )}
            </div>
          </>
        </main>
        {loading ? (
          <div className="project-wizard__content">
            <div className="page-content">
              <Loader />
            </div>
          </div>
        ) : (
          <>
            <div className="project-wizard__content">
              {activeStep === 'DOMAIN' && <DomainStep />}
              {activeStep === 'FIRM_NAME' && <FirmNameStep />}
              {activeStep === 'QUESTIONS' && <SecondStep />}
              {activeStep === 'SEARCH_VOLUME' && <ThirdStep />}
            </div>
          </>
        )}
      </div>
      <aside className="project-wizard__aside">
        <TestimonialSlider />
      </aside>
    </div>
  );
};

export default ProjectWizardPage;
